import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Image, ScrollView, Text, TouchableOpacity, View } from "react-native";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppColors } from "../../utility/AppColors";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { CommonButton } from "../../common/CommonButton";
import LiveStream from "./LiveStream";
import PlayersView from "./PlayersView";
import StreamPlaceholder from "./StreamPlaceholder";
import GetTokenPopup from "./GetTokenPopup";
import PlayerSelectionModal from "./PlayerSelectionModal";
import { useLiveStream } from "../../context/LiveStreamContext";
import { EVENT_STATUS } from "../../utility/LiveStreamSocket/LiveStreamConfig";
import Voting from "./Voting";
import DisputePopup from "./DisputePopup";
import ConnectionPopup from "./ConnectionPopup";
import { AppImages } from "../../utility/AppImages";
import { LinearGradient } from "expo-linear-gradient";

interface TokenPackage {
  tokens: number;
  price: string;
  exchangeRate: string;
}

interface StreamingViewProps {
  styles: any;
  selectedPlayer: string;
  giftData: any[];
  selectedGiftId: string | null;
  setSelectedGiftId: (id: string | null) => void;
  handleGiftSelect: (item: any) => void;
  chatOpen: boolean;
  setGetTokenPopup: (value: boolean) => void;
  getTokenPopup: boolean;
  isVoted: boolean;
  isMobile?: boolean;
  onOpenMobileGiftSheet?: () => void;
  showPlayerSelection?: boolean;
  onSelectPlayer?: (playerId: string) => void;
  selectedPlayerForGift?: string | null;
  onSendGift?: () => void;
  onCancelModal?: () => void;
  organizer?: { userID: string; profilePicture: string } | null;
  challenger?: { userID: string; profilePicture: string } | null;
  // Render keys for player reload on socket events
  organiserRenderKey?: number;
  challengerRenderKey?: number;
}

const StreamingView: React.FC<StreamingViewProps> = ({
  styles,
  selectedPlayer,
  giftData,
  selectedGiftId,
  setSelectedGiftId,
  handleGiftSelect,
  chatOpen,
  setGetTokenPopup,
  getTokenPopup,
  isVoted,
  isMobile = false,
  onOpenMobileGiftSheet,
  showPlayerSelection = false,
  onSelectPlayer,
  selectedPlayerForGift,
  onSendGift,
  onCancelModal,
  organizer,
  challenger,
  organiserRenderKey = 0,
  challengerRenderKey = 0,
}) => {
  const [isDispute, setIsDispute] = useState(false);
  const [renderKey, setRenderKey] = useState(0); // New state for forcing re-renders
  const [isPreparing, setIsPreparing] = useState(false); // New state for showing preparing message
  const [hasFirstRenderCompleted, setHasFirstRenderCompleted] = useState(false); // Track if first render is done
  const [internalTimeValid, setInternalTimeValid] = useState(false); // Internal countdown sync
  const prevOrganiserPaused = useRef(false); // Track previous organiser pause state
  const prevChallengerPaused = useRef(false); // Track previous challenger pause state
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeout15Ref = useRef<NodeJS.Timeout | null>(null);
  const timeout25Ref = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const {
    audienceData,
    userData,
    eventStartTime,
    organiserStreamPaused,
    challengerStreamPaused,
    setOrganiserStreamPaused,
    setChallengerStreamPaused,
  } = useLiveStream();
  const { eventDetails } = audienceData || { eventDetails: {} };

  useEffect(() => {
    if (eventDetails) {
      setIsDispute(!!eventDetails?.resultStatus?.dispute);
    }
  }, [eventDetails]);

  const [tokenPackages, setTokenPackages] = useState<TokenPackage[]>([
    // Bundle #1: EVENT-ONLY - Quick audience cheer (micro gift)
    // { tokens: 28, price: "£0.29", exchangeRate: "£0.01" },
    // Bundle #2: EVENT-ONLY - Multiple small gifts
    { tokens: 88, price: "£0.90", exchangeRate: "£0.01" },
    // Bundle #3: Starter - 1 Bronze belt + 27 spare (500 base + 77 bonus)
    { tokens: 577, price: "£5.99", exchangeRate: "£0.01" },
    // Bundle #4: Competitor - 2 Bronze belts + 27 spare (1,050 base + 77 bonus)
    { tokens: 1127, price: "£12.79", exchangeRate: "£0.01" },
    // Bundle #5: Champion - Midpoint bundle, best balance (2,150 base + 77 bonus)
    { tokens: 2227, price: "£26.29", exchangeRate: "£0.01" },
    // Bundle #6: Elite - Ideal for regular challengers (4,350 base + 77 bonus)
    { tokens: 4427, price: "£52.99", exchangeRate: "£0.01" },
    // Bundle #7: Legend - High-value player bundle (8,750 base + 77 bonus)
    { tokens: 8827, price: "£106.79", exchangeRate: "£0.01" },
    // Bundle #8: Pro - Ultimate value bundle (20,250 base + 77 bonus)
    { tokens: 20327, price: "£246.99", exchangeRate: "£0.01" },
  ]);

  const [selectedPackage, setSelectedPackage] = useState<TokenPackage | null>(
    null
  );

  const handlePackageSelect = (pkg: TokenPackage) => {
    setSelectedPackage(pkg);
  };

  const deviceSize = useDeviceSize();

  const EventTitle = audienceData?.eventDetails?.eventTitle || "Game Show";

  const organiserStreamCompleted =
    audienceData?.eventDetails?.streamStatus?.organiser ===
    EVENT_STATUS.COMPLETED;

  const currentDateTime = useMemo(() => new Date(), []);
  const eventDateTime = useMemo(
    () => (eventStartTime ? new Date(eventStartTime) : null),
    [eventStartTime]
  );

  // Internal countdown timer to sync with event start
  useEffect(() => {
    if (eventDateTime && !internalTimeValid) {
      const now = new Date().getTime();
      const eventTime = eventDateTime.getTime();
      const timeUntilEvent = eventTime - now;

      if (timeUntilEvent <= 0) {
        // Event time has already passed
        setInternalTimeValid(true);
      } else {
        // Set countdown to trigger when event starts
        countdownRef.current = setTimeout(() => {
          setInternalTimeValid(true);
        }, timeUntilEvent);
      }
    }

    return () => {
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [eventDateTime, internalTimeValid]);

  const isWithinValidTime = useMemo(() => {
    // Use internal countdown sync or fallback to original logic
    return (
      internalTimeValid ||
      (!!eventDateTime &&
        !isNaN(eventDateTime.getTime()) &&
        new Date().getTime() >= eventDateTime.getTime())
    );
  }, [internalTimeValid, eventDateTime]);

  const resultSubmitted =
    audienceData?.eventDetails?.resultStatus?.resultSubmitted || false;
  const dispute = audienceData?.eventDetails?.resultStatus?.dispute || false;
  const winner = audienceData?.eventDetails?.resultStatus?.winner || null;
  const eventStatus =
    audienceData?.eventDetails?.status || EVENT_STATUS.UPCOMING;
  const isEventComplete = eventStatus === EVENT_STATUS.COMPLETED;

  useEffect(() => {
    const shouldTrigger =
      eventStatus === EVENT_STATUS.LIVE ||
      (prevOrganiserPaused.current && !organiserStreamPaused) ||
      (prevChallengerPaused.current && !challengerStreamPaused);

    if (shouldTrigger && !timeoutRef.current && !hasFirstRenderCompleted) {
      setIsPreparing(true);

      timeoutRef.current = setTimeout(() => {
        setRenderKey((prev) => prev + 1);
        setIsPreparing(false);
        setHasFirstRenderCompleted(true);
      }, 10000);

      timeout15Ref.current = setTimeout(() => {
        setRenderKey((prev) => prev + 1);
      }, 15000);

      timeout25Ref.current = setTimeout(() => {
        setRenderKey((prev) => prev + 1);
      }, 25000);
    }

    prevOrganiserPaused.current = organiserStreamPaused;
    prevChallengerPaused.current = challengerStreamPaused;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (timeout15Ref.current) {
        clearTimeout(timeout15Ref.current);
        timeout15Ref.current = null;
      }
      if (timeout25Ref.current) {
        clearTimeout(timeout25Ref.current);
        timeout25Ref.current = null;
      }
      setIsPreparing(false);
    };
  }, [
    eventStatus,
    organiserStreamPaused,
    challengerStreamPaused,
    hasFirstRenderCompleted,
  ]);

  const displayMessage = useMemo(() => {
    if (eventStatus === EVENT_STATUS.UPCOMING || !isWithinValidTime) {
      return `${EventTitle} Event is About to Begin`;
    }
    if (organiserStreamCompleted) {
      if (!resultSubmitted) {
        return `${EventTitle} has come to an end and we are waiting for the results.`;
      }
      if (resultSubmitted && dispute) {
        return `${EventTitle} has come to an end and a dispute has been raised.`;
      }
      if (resultSubmitted && !dispute) {
        return `${EventTitle} has come to an end and the winner is ${winner}.`;
      }
    }
    return `${EventTitle} is live.`;
  }, [
    EventTitle,
    dispute,
    eventStatus,
    isWithinValidTime,
    organiserStreamCompleted,
    resultSubmitted,
    winner,
  ]);

  const isOrganizerSelected =
    selectedPlayer === audienceData?.eventDetails?.organiserId;

  const isResultWaiting = organiserStreamCompleted && !resultSubmitted;

  const getUserPlatform = useCallback(
    (userType: string) => {
      const platforms =
        audienceData?.eventDetails?.streamingPlatforms?.[userType] || [];
      if (platforms.includes("YouTube")) return "youtube";
      if (platforms.includes("Twitch")) return "twitch";
      return null;
    },
    [audienceData?.eventDetails?.streamingPlatforms]
  );

  const getStreamId = useCallback(
    (userType: string) => {
      if (!audienceData?.streamDetails?.[0]) return null;
      const {
        organizerYoutubeStream,
        challengerYoutubeStream,
        organizerTwitchStream,
        challengerTwitchStream,
      } = audienceData.streamDetails[0];

      const userPlatform = getUserPlatform(userType);
      if (userPlatform === "twitch" && userType === "organiser")
        return organizerTwitchStream?.liveStreamURL;
      if (userPlatform === "twitch" && userType === "challenger")
        return challengerTwitchStream?.liveStreamURL;
      if (userPlatform === "youtube" && userType === "organiser")
        return organizerYoutubeStream?.liveStreamId;
      if (userPlatform === "youtube" && userType === "challenger")
        return challengerYoutubeStream?.liveStreamId;

      return null;
    },
    [audienceData?.streamDetails, getUserPlatform]
  );

  const organizerStreamId = getStreamId("organiser");
  const challengerStreamId = getStreamId("challenger");
  const organizerPlatform = getUserPlatform("organiser");
  const challengerPlatform = getUserPlatform("challenger");

  const renderLiveStream = (
    streamId: string | null,
    platform: string | null,
    playerLabel: string,
    width: string,
    marginTop: number = 0,
    marginBottom: number = 0
  ) => {
    const isOrganizer = playerLabel === audienceData?.eventDetails?.organiserId;
    const isPaused = isOrganizer
      ? organiserStreamPaused
      : challengerStreamPaused;
    const pauseMessage = isOrganizer
      ? "Organizer's stream is paused due to an error."
      : "Challenger's stream is paused due to an error.";
    // Use external render key for socket-triggered reloads
    const externalRenderKey = isOrganizer
      ? organiserRenderKey
      : challengerRenderKey;

    return (
      <View
        style={{
          position: "relative",
          backgroundColor: "#111827",
          padding: 4,
          marginHorizontal: 4,
          overflow: "hidden",
          borderWidth: 0.3,
          borderColor: AppColors.koBrandRed || "#DC2626",
          borderRadius: 4,
          marginTop,
          marginBottom,
        }}
      >
        {/* Show player name only when both players selected or not in mobile view */}
        {(selectedPlayer === "both" || !isMobile) && (
          <View
            style={{
              borderBottomWidth: 0.3,
              borderBottomColor: "rgba(139, 139, 139, 1)",
              paddingHorizontal: 8,
              paddingVertical: 6,
              marginBottom: 8,
              alignSelf: "flex-start",
            }}
          >
            <Text style={{ color: "white", fontWeight: "600", fontSize: 14 }}>
              {playerLabel}
            </Text>
          </View>
        )}
        {isPaused ? (
          <View
            style={{
              justifyContent: "center",
              alignItems: "center",
              aspectRatio: 16 / 9, // Maintain 16:9 aspect ratio
              backgroundColor: AppColors.black_18151b,
              borderRadius: 8,
            }}
          >
            <Text style={{ color: "white" }}>Stream paused</Text>
          </View>
        ) : streamId && isWithinValidTime ? (
          <View
            style={{
              borderRadius: 8,
              overflow: "hidden",
              backgroundColor: AppColors.black_121212,
              aspectRatio: 16 / 9,
            }}
          >
            <LiveStream
              key={`${streamId}-${platform}-${renderKey}-${externalRenderKey}`}
              platform={platform}
              streamId={streamId}
              height="100%"
              width="100%"
              preventRedirect={true}
              style={{
                borderRadius: 8,
                overflow: "hidden",
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            />
          </View>
        ) : (
          <StreamPlaceholder />
        )}
        {isPaused && (
          <View
            style={{
              position: "absolute",
              top: 25,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <ConnectionPopup
              onClose={() =>
                isOrganizer
                  ? setOrganiserStreamPaused(false)
                  : setChallengerStreamPaused(false)
              }
              pauseMessage={pauseMessage}
            />
          </View>
        )}
      </View>
    );
  };

  const isLiveStream = eventStatus === EVENT_STATUS.LIVE;

  return (
    <View
      style={{
        flex:
          deviceSize === DEVICE_SIZES.XS ||
          deviceSize === DEVICE_SIZES.SM ||
          deviceSize === DEVICE_SIZES.MD ||
          deviceSize === DEVICE_SIZES.LG
            ? 1
            : chatOpen
            ? 0.6
            : 0.8,
        marginVertical: deviceSize === DEVICE_SIZES.XL ? 12 : 0,
        backgroundColor: "#000000",
        flexDirection: "column",
      }}
    >
      {selectedPlayer === "both" ? (
        organizerStreamId && isWithinValidTime && !organiserStreamCompleted ? (
          <View
            style={{
              backgroundColor:
                deviceSize === DEVICE_SIZES.XL ||
                deviceSize === DEVICE_SIZES.LG ||
                deviceSize === DEVICE_SIZES.MD
                  ? "black"
                  : "transparent",
              paddingTop:
                deviceSize === DEVICE_SIZES.XL ||
                deviceSize === DEVICE_SIZES.LG ||
                deviceSize === DEVICE_SIZES.MD
                  ? 0
                  : 0,
              paddingHorizontal:
                deviceSize === DEVICE_SIZES.XL ||
                deviceSize === DEVICE_SIZES.LG ||
                deviceSize === DEVICE_SIZES.MD
                  ? 12
                  : 0,
            }}
          >
            <LinearGradient
              colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                borderRadius:
                  deviceSize === DEVICE_SIZES.XL ||
                  deviceSize === DEVICE_SIZES.LG ||
                  deviceSize === DEVICE_SIZES.MD
                    ? 8
                    : 0,
                borderWidth: 1,
                borderColor: "#374151",
                padding:
                  deviceSize === DEVICE_SIZES.SM ||
                  deviceSize === DEVICE_SIZES.XS
                    ? 4
                    : 12,
              }}
            >
              <View
                style={{
                  flexDirection:
                    deviceSize === DEVICE_SIZES.SM ||
                    deviceSize === DEVICE_SIZES.XS
                      ? "column"
                      : "row",
                  gap: 8,
                }}
              >
                <View style={{ flex: 1 }}>
                  {renderLiveStream(
                    organizerStreamId,
                    organizerPlatform,
                    audienceData?.eventDetails?.organiserId || "",
                    "100%",
                    0,
                    0
                  )}
                </View>
                <View style={{ flex: 1 }}>
                  {renderLiveStream(
                    challengerStreamId,
                    challengerPlatform,
                    audienceData?.eventDetails?.challengerId || "",
                    "100%",
                    0,
                    0
                  )}
                </View>
              </View>
            </LinearGradient>
          </View>
        ) : (
          <View
            style={{
              backgroundColor:
                deviceSize === DEVICE_SIZES.XL ||
                deviceSize === DEVICE_SIZES.LG ||
                deviceSize === DEVICE_SIZES.MD
                  ? "black"
                  : "transparent",
              paddingTop:
                deviceSize === DEVICE_SIZES.XL ||
                deviceSize === DEVICE_SIZES.LG ||
                deviceSize === DEVICE_SIZES.MD
                  ? 12
                  : 0,
              paddingHorizontal:
                deviceSize === DEVICE_SIZES.XL ||
                deviceSize === DEVICE_SIZES.LG ||
                deviceSize === DEVICE_SIZES.MD
                  ? 12
                  : 0,
            }}
          >
            <LinearGradient
              colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                borderRadius: 8,
                borderWidth: 1,
                borderColor: "#374151",
                padding:
                  deviceSize === DEVICE_SIZES.SM ||
                  deviceSize === DEVICE_SIZES.XS
                    ? 4
                    : 12,
              }}
            >
              <View>
                <View style={{ marginBottom: 20 }}>
                  {isVoted ? (
                    <Voting
                      pollEndTime={
                        audienceData?.eventDetails?.pollEndTime ||
                        "2025-02-18T20:00:00Z"
                      }
                    />
                  ) : isDispute ? (
                    <DisputePopup
                      disputeEndTime={
                        audienceData?.eventDetails?.disputeEndTime ||
                        "2025-02-18T20:00:00Z"
                      }
                    />
                  ) : (
                    <PlayersView
                      isStreaming={true}
                      resultWaiting={isResultWaiting}
                    />
                  )}
                </View>
              </View>
            </LinearGradient>
          </View>
        )
      ) : (
        <View
          style={{
            backgroundColor:
              deviceSize === DEVICE_SIZES.XL ||
              deviceSize === DEVICE_SIZES.LG ||
              deviceSize === DEVICE_SIZES.MD
                ? "black"
                : "transparent",
            paddingTop:
              deviceSize === DEVICE_SIZES.XL ||
              deviceSize === DEVICE_SIZES.LG ||
              deviceSize === DEVICE_SIZES.MD
                ? 0
                : 0,
            paddingHorizontal:
              deviceSize === DEVICE_SIZES.XL ||
              deviceSize === DEVICE_SIZES.LG ||
              deviceSize === DEVICE_SIZES.MD
                ? 12
                : 0,
            flex:
              deviceSize === DEVICE_SIZES.XL && selectedPlayer === "both"
                ? 0.65
                : undefined,
          }}
        >
          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "#374151",
              padding:
                deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
                  ? 4
                  : 12,
              flex: selectedPlayer === "both" ? 1 : undefined,
            }}
          >
            <View
              style={{
                alignItems:
                  deviceSize === DEVICE_SIZES.XL ||
                  deviceSize === DEVICE_SIZES.LG ||
                  deviceSize === DEVICE_SIZES.MD
                    ? "center"
                    : "stretch",
                flex: selectedPlayer === "both" ? 1 : undefined,
              }}
            >
              {isLiveStream ? (
                (() => {
                  const selectedPlatform = isOrganizerSelected
                    ? getUserPlatform("organiser")
                    : getUserPlatform("challenger");

                  const selectedStreamId = isOrganizerSelected
                    ? getStreamId("organiser")
                    : getStreamId("challenger");

                  const label = isOrganizerSelected
                    ? audienceData?.eventDetails?.organiserId || ""
                    : audienceData?.eventDetails?.challengerId || "";

                  const isDesktop =
                    deviceSize === DEVICE_SIZES.XL ||
                    deviceSize === DEVICE_SIZES.LG ||
                    deviceSize === DEVICE_SIZES.MD;

                  return (
                    <View
                      style={
                        isDesktop
                          ? {
                              width: "100%",
                              maxWidth:
                                selectedPlayer === "both" ? "85%" : "100%",
                              ...(selectedPlayer === "both"
                                ? { aspectRatio: 16 / 9 }
                                : {}),
                            }
                          : {}
                      }
                    >
                      {renderLiveStream(
                        selectedStreamId,
                        selectedPlatform,
                        label,
                        "100%",
                        0,
                        0
                      )}
                    </View>
                  );
                })()
              ) : (
                <View style={{ marginTop: 10 }}>
                  {isVoted ? (
                    <Voting
                      pollEndTime={
                        audienceData?.eventDetails?.pollEndTime ||
                        "2025-02-18T20:00:00Z"
                      }
                    />
                  ) : isDispute ? (
                    <DisputePopup
                      disputeEndTime={
                        audienceData?.eventDetails?.disputeEndTime ||
                        "2025-02-18T20:00:00Z"
                      }
                    />
                  ) : (
                    <PlayersView
                      isStreaming={true}
                      resultWaiting={isResultWaiting}
                    />
                  )}
                </View>
              )}
            </View>
          </LinearGradient>
        </View>
      )}

      {/* Desktop/Tablet Gifting UI for MD, LG, XL devices */}
      {deviceSize === DEVICE_SIZES.XL ||
      deviceSize === DEVICE_SIZES.LG ||
      deviceSize === DEVICE_SIZES.MD ? (
        <View
          style={{
            backgroundColor: "black",
            paddingHorizontal: 12,
            flex: 1,
            flexDirection: "column",
          }}
        >
          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "#374151",
              padding: 12,
              marginTop: 6,
            }}
          >
            <View
              style={{
                flexDirection:
                  deviceSize === DEVICE_SIZES.MD ? "column" : "row",
                justifyContent:
                  deviceSize === DEVICE_SIZES.MD
                    ? "flex-start"
                    : "space-between",
                gap: deviceSize === DEVICE_SIZES.MD ? 15 : 12,
                alignItems: "center",
              }}
            >
              <Text style={[AppFontStyle.BOLD_ANYBODY_18]}>
                Select gift and cheer on the players
              </Text>
              <View
                style={{
                  flexDirection:
                    deviceSize === DEVICE_SIZES.MD ? "column" : "row",
                  gap: deviceSize === DEVICE_SIZES.MD ? 12 : 10,
                  flex: deviceSize === DEVICE_SIZES.MD ? 0 : 1,
                  justifyContent: "space-between",
                  alignItems:
                    deviceSize === DEVICE_SIZES.MD ? "flex-start" : "center",
                }}
              >
                <View style={{ flexDirection: "row", gap: 10 }}>
                  <View style={{ justifyContent: "center" }}>
                    <Text style={AppFontStyle.BOLD_ANYBODY_12}>
                      Token Balance
                    </Text>
                  </View>
                  <View
                    style={{
                      flexDirection: "row",
                      gap: 3,
                      alignItems: "center",
                    }}
                  >
                    <Image
                      source={AppImages.tokensIcon}
                      style={{ height: 19, width: 19 }}
                      resizeMode="contain"
                    />
                    <Text style={AppFontStyle.BOLD_ANYBODY_12}>
                      {userData?.tokens ?? 0}
                    </Text>
                  </View>
                </View>
                <View style={{ flexDirection: "row", gap: 10 }}>
                  <TouchableOpacity
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 8,
                      paddingHorizontal: 8,
                      paddingVertical: 6,
                      backgroundColor: AppColors.appBlackColor_1e1e1e,
                      borderRadius: 6,
                      borderColor: AppColors.grayColor_505050,
                      borderWidth: 1,
                    }}
                    onPress={() => setGetTokenPopup(!getTokenPopup)}
                  >
                    <Image
                      source={AppImages.icRechargeIcon}
                      style={{ height: 15, width: 15 }}
                      resizeMode="contain"
                    />
                    <Text style={AppFontStyle.BOLD_ANYBODY_8}>Recharge </Text>
                    {!getTokenPopup ? (
                      <Image
                        source={AppImages.arrowRight}
                        style={{ height: 20, width: 15 }}
                        resizeMode="contain"
                      />
                    ) : (
                      <Image
                        source={AppImages.arrowDown}
                        style={{ height: 20, width: 10 }}
                        resizeMode="contain"
                      />
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </LinearGradient>
          <View
            style={{
              backgroundColor: "black",
              marginTop: 6,
              flex: 1,
              position: "relative",
            }}
          >
            <LinearGradient
              colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                borderRadius: 8,
                borderWidth: 1,
                borderColor: "#374151",
                padding: 16,
                flex: 1,
                opacity: isEventComplete ? 0.5 : 1,
              }}
              pointerEvents={isEventComplete ? "none" : "auto"}
            >
              <ScrollView
                contentContainerStyle={[
                  styles.gridContainer,
                  { paddingHorizontal: 0, marginTop: 6 },
                ]}
                style={{ flex: 1 }}
                showsVerticalScrollIndicator={true}
                showsHorizontalScrollIndicator={false}
              >
                {giftData.map((item) => {
                  const isSelected = item.id === selectedGiftId;
                  return (
                    <TouchableOpacity
                      key={`${item.id ?? item.giftName}`}
                      onPress={() => {
                        // Simply call with gift ID
                        setSelectedGiftId(item.id);
                      }}
                      style={[
                        styles.itemContainer,
                        {
                          borderColor: isSelected
                            ? AppColors.koBrandRed
                            : "#9CA3AF",
                          borderWidth: isSelected ? 2 : 1,
                          borderRadius: 4,
                          overflow: "hidden",
                        },
                      ]}
                    >
                      <LinearGradient
                        colors={["#1F2937", "#111827"]}
                        style={{ flex: 1 }}
                      >
                        <View style={styles.giftContent}>
                          <Image
                            source={item.gift}
                            style={styles.giftImage}
                            resizeMode="contain"
                          />
                          <Text style={styles.giftName}>{item.giftName}</Text>
                        </View>

                        <View style={styles.priceContainer}>
                          <Image
                            source={item.image}
                            style={styles.priceIcon}
                            resizeMode="contain"
                          />
                          <Text
                            style={[
                              AppFontStyle.BOLD_ANYBODY_12,
                              { color: "white" },
                            ]}
                          >
                            {item.price}
                          </Text>
                        </View>
                      </LinearGradient>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </LinearGradient>

            {showPlayerSelection && (
              <View
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  justifyContent: "center",
                  alignItems: "center",
                  paddingHorizontal: 20,
                  zIndex: 99999,
                }}
              >
                <TouchableOpacity
                  activeOpacity={1}
                  onPress={() => {
                    if (onCancelModal) onCancelModal();
                  }}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.3)",
                  }}
                />
                <View pointerEvents="box-none" style={{ zIndex: 1 }}>
                  <PlayerSelectionModal
                    visible={showPlayerSelection}
                    onClose={() => {
                      console.log("onSendGift called:", onSendGift);
                      if (onSendGift) onSendGift();
                    }}
                    onCancel={() => {
                      console.log(
                        "onCancelModal from StreamingView:",
                        onCancelModal
                      );
                      if (onCancelModal) onCancelModal();
                    }}
                    organizer={organizer || null}
                    challenger={challenger || null}
                    onSelectPlayer={onSelectPlayer || (() => {})}
                    selectedPlayerId={selectedPlayerForGift || null}
                  />
                </View>
              </View>
            )}

            {/* Token Popup Overlay */}
            {getTokenPopup && (
              <View
                style={{
                  position: "absolute",
                  top: selectedPlayer === "both" ? -12 : -75,
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                }}
              >
                <View style={{ position: "relative" }}>
                  <TouchableOpacity
                    onPress={() => setGetTokenPopup(false)}
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 1001,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      borderRadius: 20,
                      width: 32,
                      height: 32,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Image
                      source={AppImages.icClose}
                      style={{ width: 16, height: 16, tintColor: "white" }}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                  <GetTokenPopup
                    tokenPackages={tokenPackages}
                    selectedPackage={selectedPackage}
                    handlePackageSelect={handlePackageSelect}
                    boxRightMargin={0}
                    width={"100%"}
                  />
                </View>
              </View>
            )}
          </View>
        </View>
      ) : null}
    </View>
  );
};

export default StreamingView;
